<template>
  <section class="gs-fulfillment-summary">
    <div class="gs-fulfillment-summary__heading">
      <h3>{{ title }}</h3>
      <strong>{{ items.length }}</strong>
    </div>
    <template v-if="items.length">
      <p v-if="!concealed" class="gs-fulfillment-summary__preview" :title="items[0]">{{ items[0] }}</p>
      <button
        ref="reveal"
        type="button"
        class="gs-fulfillment-summary__reveal"
        :aria-label="`${concealed ? 'Reveal' : 'View'} ${title.toLowerCase()}`"
        aria-haspopup="dialog"
        @click="isOpen = true"
      >{{ concealed ? 'Click to reveal' : 'View details' }}</button>
    </template>
    <p v-else class="gs-fulfillment-summary__empty">{{ emptyText }}</p>

    <gs-modal v-model="isOpen" :title="title" :subtitle="pricePointName" size="lg" @keydown.native="trapFocus">
      <div class="gs-fulfillment-summary__dialog">
        <ol class="gs-fulfillment-summary__list">
          <li v-for="(item, index) in items" :key="index" class="gs-fulfillment-summary__item">
            <div class="gs-fulfillment-summary__value">
              <strong v-if="concealed">Webhook {{ index + 1 }}</strong>
              <span>{{ item }}</span>
            </div>
            <gs-button
              v-if="concealed"
              variant="ghost"
              size="icon"
              :aria-label="`Copy webhook ${index + 1}`"
              :title="copiedIndex === index ? 'Copied' : 'Copy webhook URL'"
              @click="copyWebhook(item, index)"
            ><gs-icon :name="copiedIndex === index ? 'check' : 'copy'" size="18" /></gs-button>
          </li>
        </ol>
        <p class="gs-fulfillment-summary__feedback" role="status" aria-live="polite">{{ copyFeedback }}</p>
      </div>
    </gs-modal>
  </section>
</template>

<script>
export default {
  name: 'FulfillmentSummary',
  props: {
    title: { type: String, required: true },
    items: { type: Array, default: () => [] },
    emptyText: { type: String, default: '' },
    pricePointName: { type: String, default: '' },
    concealed: Boolean,
  },
  data() {
    return { isOpen: false, copiedIndex: null, copyFeedback: '' };
  },
  watch: {
    isOpen(value) {
      this.copiedIndex = null;
      this.copyFeedback = '';
      if (!value) this.$nextTick(() => { if (this.$refs.reveal) this.$refs.reveal.focus(); });
    },
  },
  methods: {
    async copyWebhook(item, index) {
      try {
        await this.$copyText(item);
        if (!this.isOpen) return;
        this.copiedIndex = index;
        this.copyFeedback = `Webhook ${index + 1} copied.`;
      } catch (error) {
        if (!this.isOpen) return;
        this.copiedIndex = null;
        this.copyFeedback = 'Could not copy. Select the URL to copy it manually.';
      }
    },
    trapFocus(event) {
      if (event.key !== 'Tab') return;
      const dialog = event.currentTarget.querySelector('[role="dialog"]');
      const buttons = dialog && dialog.querySelectorAll('button:not([disabled])');
      if (!buttons || !buttons.length) return;
      const first = buttons[0];
      const last = buttons[buttons.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
  },
};
</script>

<style scoped>
.gs-fulfillment-summary { min-width: 0; padding: 14px 0 0; border-top: 1px solid var(--gs-border); }
.gs-fulfillment-summary__heading { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; margin-bottom: 10px; }
.gs-fulfillment-summary__heading h3 { min-width: 0; margin: 0; color: var(--gs-muted-foreground); font-size: 12px; font-weight: 600; line-height: 1.5; overflow-wrap: anywhere; }
.gs-fulfillment-summary__heading strong { flex-shrink: 0; color: var(--gs-foreground); font-size: 18px; line-height: 1.4; }
.gs-fulfillment-summary__preview,
.gs-fulfillment-summary__empty { margin: 0; color: var(--gs-muted-foreground); font-size: 13px; line-height: 1.5; overflow-wrap: anywhere; }
.gs-fulfillment-summary__preview { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; color: var(--gs-foreground); }
.gs-fulfillment-summary__reveal { display: block; min-height: 44px; max-width: 100%; padding: 8px 0; color: var(--gs-active); background: transparent; border: 0; font: inherit; font-size: 13px; font-weight: 600; text-align: left; white-space: normal; cursor: pointer; }
.gs-fulfillment-summary__reveal:hover { text-decoration: underline; }
.gs-fulfillment-summary__reveal:focus-visible { outline: 2px solid var(--gs-active); outline-offset: 3px; }
.gs-fulfillment-summary__dialog { padding: 20px 22px; }
.gs-fulfillment-summary__list { margin: 0; padding: 0; list-style: none; }
.gs-fulfillment-summary__item { display: flex; align-items: flex-start; gap: 12px; min-width: 0; padding: 16px 0; border-bottom: 1px solid var(--gs-border); }
.gs-fulfillment-summary__item:first-child { padding-top: 0; }
.gs-fulfillment-summary__item:last-child { border-bottom: 0; }
.gs-fulfillment-summary__value { flex: 1; min-width: 0; color: var(--gs-foreground); font-size: 14px; line-height: 1.6; overflow-wrap: anywhere; white-space: pre-wrap; }
.gs-fulfillment-summary__value strong { display: block; margin-bottom: 6px; color: var(--gs-muted-foreground); font-size: 12px; }
.gs-fulfillment-summary__item > .gs-button { flex: 0 0 40px; }
.gs-fulfillment-summary__feedback { min-height: 20px; margin: 8px 0 0; color: var(--gs-muted-foreground); font-size: 13px; line-height: 1.5; }
.gs-fulfillment-summary ::v-deep .gs-modal__subtitle { overflow-wrap: anywhere; }
.gs-fulfillment-summary ::v-deep .gs-modal__header > .gs-button { flex-shrink: 0; }
</style>
